<script setup>

    const { loggedIn, user, fetch: refreshSession } = useUserSession();

    const props = defineProps({
        session: {
            type: Object,
            required: true,
        },
        layout: {
            type: String,
            required: false,
            default: 'wide',
        },
        show_sponsors: {
            type: Boolean,
            required: false,
            default: false,
        },
        show_tags: {
            type: Boolean,
            required: false,
            default: true,
        },
        hide_featured: {
            type: Boolean,
            required: false,
            default: false,
        },
        useA: {
            type: Boolean,
            required: false,
            default: false,
        },
        teal: {
            type: Boolean,
            required: false,
            default: false,
        },
        full: {
            type: Boolean,
            required: false,
            default: false,
        },
        disableLink: {
            type: Boolean,
            required: false,
            default: false,
        },
    });

    const cardHover = ref(false);
    const isFavorite = ref(null);
    const busy = ref(false);

    const emit = defineEmits(['load', 'load_speaker']);

    const thumbailForSpeaker = (speaker) => {
        if (speaker.profile_image?.length && speaker.profile_image[0].thumbnails?.large) {
            return speaker.profile_image[0].thumbnails.large;
        }
        return null;
    };

    const zIndex = (index) => {
        return props.session.speakers?.length - index;
    };

    const speakers = computed(() => {
        if (!props.session.speakers?.length) return [];
        return props.session.speakers.slice(0, 5);
    });

    const handleSkillClick = async (level) => {
        if (!props.sessionsUrl) await navigateTo({ query: { level: level } });
        else if (props.sessionsUrl && props.sessionsUrl.includes('?')) await navigateTo({ path: props.sessionsUrl, query: { level: level } });
        else if (props.sessionsUrl) await navigateTo({ path: props.sessionsUrl, query: { level: level } });
    };

    const handleFavoriteClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'favorite' } });
            return;
        }
        try {
            busy.value = true;
            isFavorite.value = true;
            await $fetch('/api/v1/user/favorite', {
                method: 'POST',
                body: { sessionId: props.session.unique_id, email: user.value.email, ticketId: user.value.ticketId },
            });
            await refreshSession();
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.');
            isFavorite.value = null;
        } finally {
            busy.value = false;
        }
    };

    const is_favorited = computed(() => {
        // Check if user.favorites exists and contains the session unique_id
        const userHasFavorited = user.value?.favorites?.includes(props.session.unique_id);
        return isFavorite.value === null ? userHasFavorited : isFavorite.value;
    });

    const handleUnfavoriteClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login' });
            return;
        }
        try {
            busy.value = true;
            isFavorite.value = false;
            await $fetch('/api/v1/user/unfavorite', {
                method: 'POST',
                body: { sessionId: props.session.unique_id, email: user.value.email, ticketId: user.value.ticketId },
            });
            await refreshSession();
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.');
            isFavorite.value = null;
        } finally {
            busy.value = false;
        }
    };

    const handleTitleClick = async (event) => {
        if (props.disableLink) {
            event.preventDefault()
            emit('load', props.session)
            return false
        }
    };

    const handleSpeakerClick = async (event, speaker) => {
        if (props.disableLink) {
            event.preventDefault()
            emit('load_speaker', speaker)
            return false
        }
    };

    const handleReservationClick = async () => {
        await navigateTo({ path: `/reservations/${props.session.id}` });
        return;
    };

</script>
<template>
    <div class="session-card" :class="[teal ? 'teal' : '', cardHover ? 'hover' : '', busy ? 'busy-card' : '', layout]">
        <div class="bugs" v-if="session.reservations_required || session.featured">
            <span class="required-reservation" v-if="session.reservations_required">Reservation Required</span>
            <span class="featured" v-if="!hide_featured && session.featured">Featured</span>
        </div>
        <p class="time" v-if="full && session.start_date_and_time">
            <template v-for="part in session.display_date">
                {{ part }}
                <span>&bull;</span>
            </template>
        </p>
        <MessLink
            :href="`/sessions/${session.id}`"
            :label="session.title"
            @mouseover="cardHover = true"
            @mouseleave="cardHover = false"
            @click="handleTitleClick"
            external>
            <h3 v-html="session.title"></h3>
            <p class="description" v-html="session.description" v-if="layout === 'wide'"></p>
            <p class="recommendation-reason" v-if="session.recommendationReason" :data-score="session.score">
                <UIcon name="local-assistant" class="breeze-icon" />
                <span v-html="session.recommendationReason"></span>
            </p>
        </MessLink>
        <template v-if="session.speakers?.length > 0">
            <div class="speakers">
                <ul class="speaker-imgs">
                    <template v-if="speakers" v-for="(speaker, index) in speakers" :key="speaker.id">
                        <li
                            v-if="speaker.profile_image?.length && speaker.profile_image[0].thumbnails"
                            :style="`--zIndex: ${zIndex(index)}`">
                            <MessLink :href="`/speakers/${speaker.id}`" class="speaker-link" @click="handleSpeakerClick(speaker)" :external="disableLink">
                                <MessImage
                                    v-if="speaker.profile_image[0]"
                                    :image="thumbailForSpeaker(speaker)"
                                    lazy
                                    class="speaker-image"
                                    :alt="`${speaker.name} profile photo`" />
                            </MessLink>
                        </li>
                        <li v-else>
                            <img
                                src="@/assets/img/speaker-default-small.jpg"
                                alt="Generic person image"
                                loading="lazy" />
                        </li>
                    </template>
                </ul>
                <ul class="speaker-names">
                    <li v-if="speakers" v-for="(speaker, index) in speakers" :key="speaker.id">
                        <MessLink :href="`/speakers/${speaker.id}`" class="speaker-link">
                            {{ speaker.name }}
                        </MessLink>
                        <span class="comma" v-if="index < speakers.length - 1" inert>,&nbsp;</span>
                    </li>
                </ul>
            </div>
        </template>
        <p
            class="skill-level"
            v-if="session.skill_level"
            @click.prevent="handleSkillClick(session.skill_level)"
            v-html="session.skill_level"></p>
        <SessionsTags v-if="show_tags" :session="session" />
        <div class="sponsors" v-if="show_sponsors && (session.sponsors?.length || session.also_sponsors?.length)">
            <FragmentsSponsorsSponsoredBy :sponsors="session.sponsors" />
            <FragmentsSponsorsAlsoSponsoredBy :sponsors="session.also_sponsors" />
        </div>
        <div class="actions" v-if="full && session.unique_id">
            <ClientOnly>
                <UButton
                    v-if="is_favorited"
                    :variant="teal ? 'sage' : 'transparent'"
                    icon="local-heart-filled"
                    @click.prevent="handleUnfavoriteClick"
                    :class="teal ? 'text-orange-for-teal' : 'text-orange'"
                    >Favorited</UButton
                >
                <UButton
                    v-else
                    :variant="teal ? 'sage' : 'transparent'"
                    icon="local-heart-empty"
                    @click.prevent="handleFavoriteClick"
                    >Add to Favorites</UButton
                >
                <UButton
                    v-if="session.reservations_required"
                    :variant="teal ? 'sage' : 'transparent'"
                    icon="local-seat-empty"
                    @click="handleReservationClick"
                    >Save a Seat</UButton
                >
            </ClientOnly>
        </div>
    </div>
</template>

<style lang="scss">
    .session-card {
        background: white;
        padding: var(--box-padding-lr-m);
        border-radius: var(--radius);
        overflow: hidden;
        scroll-snap-align: start;
        transition: box-shadow 250ms ease-in-out;
        display: flex;
        flex-direction: column;

        &.hover {
            box-shadow: inset 0 0 0 1px var(--cream-light);
        }

        .bugs {
            display: flex;
            align-items: center;
            gap: 1.5rem;

            .featured {
                margin-bottom: 1rem;
            }
        }

        @media (min-width: $tablet) {
            padding: 2.5rem;
        }

        .required-reservation {
            width: fit-content;
            display: flex;
            color: white;
            font-size: 0.75rem;
            font-weight: 500;
            line-height: 1.1875rem;
            letter-spacing: 0.0375rem;
            text-transform: uppercase;
            padding: 0.375rem 1rem;
            background-color: var(--orange-dark);
            border-radius: var(--full-radius);
            margin-bottom: 0.9375rem;
        }

        &.teal {
            &.hover {
                box-shadow: inset 0 0 0 1px var(--teal-ring);
            }

            .required-reservation {
                background-color: var(--orange);
            }
        }

        .sponsors {
            margin-top: 1rem;
        }

        .featured {
            display: block;
            text-transform: uppercase;
            font-size: 0.6875rem;
            font-weight: 500;
            color: var(--orange-dark);
            line-height: 1.25rem;
            letter-spacing: 0.040625rem;
            margin-bottom: 0.5625rem;

            @media (min-width: $tablet) {
                font-size: 0.8125rem;
            }
        }

        .time {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.75rem;
            font-weight: 500;
            line-height: 1.625rem;
            margin-bottom: 0.5rem;
            letter-spacing: 0rem;

            span {
                color: var(--teal-light);
                &:last-child {
                    display: none;
                }
            }

            @media (min-width: $tablet) {
                font-size: 0.9375rem;
            }
        }

        a.mess-link:hover {
            color: var(--orange-dark);
        }

        h3 {
            font-size: 1.6875rem;
            font-weight: 500;
            line-height: 2rem;
            letter-spacing: -0.03375rem;
            margin-bottom: 0.8125rem;

            a:hover {
                color: var(--orange-dark);
            }

            @media (min-width: $tablet) {
                font-size: 2.25rem;
                line-height: 3rem;
                letter-spacing: -0.045rem;
                margin-bottom: 1.125rem;
            }
        }

        .description {
            font-size: 0.9375rem;
            font-weight: 300;
            line-height: 1.4375rem;
            letter-spacing: 0.01875rem;
            margin-bottom: 1.4375rem;

            display: -webkit-box;
            -webkit-line-clamp: 4;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;

            &.hover {
                color: var(--orange-dark);
            }

            @media (min-width: $tablet) {
                line-height: 1.5625rem;
                margin-bottom: 1.875rem;
            }
        }

        .speaker-names {
            font-size: 1rem;
            font-weight: 500;
            line-height: 1.625rem;
            letter-spacing: -0.02rem;

            display: flex;
            flex-wrap: wrap;
            flex-direction: row;
            align-items: center;
            margin-bottom: 0;
        }

        &.teal {
            .speaker-names a {
                @include hover {
                    color: var(--orange-for-teal);
                }
            }
        }

        .recommendation-reason {
            color: var(--magenta);
            font-size: 0.8125rem;
            font-style: normal;
            font-weight: 300;
            line-height: 1.1875rem;
            letter-spacing: 0.01625rem;
            margin-bottom: 1.75rem;
            padding: 1.25rem;
            background: linear-gradient(90deg, #FFF0F4 0%, #FFE9E0 100%);
            border-radius: var(--radius);
            
            .breeze-icon {
                width: 0.875rem;
                height: 0.875rem;
                margin-right: 0.5rem;
                vertical-align: middle;
            }
        }

        .speakers {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            margin-bottom: 0;
            gap: 1.1875rem;

            @media (min-width: $tablet) {
                margin-bottom: 1.3125rem;
                gap: 1.3125rem;
            }
        }

        &.wide {
            .speakers {
                margin-bottom: 1.1875rem;

                @media (min-width: $tablet) {
                    margin-bottom: 2.5625rem;
                }
            }
        }

        &.teal {
            .speaker-imgs li {
                border-color: var(--teal-darkish);
            }
        }

        .skill-level {
            width: fit-content;
            font-size: 0.6875rem;
            font-weight: 400;
            line-height: 0.6875rem;
            letter-spacing: 0.034375rem;
            text-transform: uppercase;
            margin-bottom: 1.125rem;
            padding: 0.4375rem 0.75rem 0.4375rem 0.75rem;
            border-radius: var(--radius);
            border: 1px solid var(--teal-darkish);
            cursor: pointer;

            @include hover {
                background-color: var(--teal-darkish);
                color: white;
            }
        }

        &.teal {
            .skill-level {
                border-color: white;

                @include hover {
                    background-color: var(--orange);
                    border-color: var(--orange);
                    color: white;
                }
            }
        }

        .session-tags-list.tags {
            line-height: 0.875rem;
            margin-bottom: 1.375rem;

            a button p {
                font-size: 0.6875rem;
                line-height: 0.9375rem;
                letter-spacing: 0.034375rem;
            }
        }

        .actions {
            display: flex;
            flex-direction: column;
            gap: 1.22875rem;
            margin-top: auto;
            padding-top: 1.3125rem;

            @media (min-width: $tablet) {
                gap: 2.1875rem;
                flex-direction: row;
            }
        }

        &.teal {
            .actions button:hover {
                color: white;
            }
        }

        .speaker-imgs {
            display: flex;
            flex-direction: row;
            align-items: center;

            li {
                width: 4.5rem;
                height: 4.5rem;
                border-radius: var(--radius); // make it a circle
                z-index: var(--zIndex);
                border: 2px solid white;
                overflow: hidden;
                aspect-ratio: 1 / 1;

                &:not(:first-child) {
                    margin-left: -0.375rem;
                }

                a {
                    @include hover {
                        img {
                            transform: scale(var(--scale));
                        }
                    }
                }

                img {
                    width: 4.5rem;
                    height: 4.5rem;
                    border-radius: var(--radius);
                    object-position: center;
                    object-fit: cover;
                    transition: transform 250ms ease-in-out;
                    aspect-ratio: 1 / 1;
                }
            }
        }
    }
</style>
